import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../../ChatConsoleView.module.scss';
import chatLoader from '../assets/chat-loader.gif';
import UserRequest from '../user-request/UserRequest';
import BotResponse from '../bot-response/BotResponse';
import ChatInput from '../chat-input/ChatInput';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import ChatHeader from '../chat-header/ChatHeader';
import { useUserContext } from 'app-context/user/UserContext';

const validationSchema = Yup.object().test(
  'at-least-one-field',
  'At least one field (Question, image, file, or audio) is required',
  (values) => {
    if (!values) return false;
    return (
      (values.Question && values.Question.trim().length > 0) ||
      (values.images && values.images.length > 0) ||
      (values.files && values.files.length > 0) ||
      !!values.audio
    );
  }
);
const ChatBody = ({
  isOpen,
  isFetchingChat,
  isChatting,
  isWorkFlow,
  guestChat,
  userData,
  isRequesting,
  hasSidebar,
  chatId,
  bot,
  isSubmitChatLoading,
  capture,
  handleSubmit,
  className,
  chattingClassName,
  inputContainerClass,
  bodyClassName,
  forceResetForm,
  setGuestChat,
  setCapture,
  setSuggestedQuestions,
  suggestedQuestions,
  botSettingsData,
  isExpanded,
  restartForm,
  disableConsole,
  isConversation,
  idleTime,
  handleIdleSubmit,
  triggerNewConversation,
  chatBodyClassname,
}) => {
  const { selectedBot } = useUserContext();
  //// for testing
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);
  //// for testing

  const idleTimerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const responseRef = useRef(null);
  const suggestedQuestionsRef = useRef(null);
  const [hasActiveForm, setHasActiveForm] = useState(false);
  const [isIdleCalledLast, setIsIdleCalledLast] = useState(false);

  const formik = useFormik({
    initialValues: { Question: '', images: [], files: [], audio: null },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      responseRef.current.scrollTo({
        top: responseRef.current.scrollHeight,
        behavior: 'smooth',
      });

      handleSubmit(values);
      formik.setFieldValue('audio', null);

      resetForm();
      setIsIdleCalledLast(false);
    },
  });

  useEffect(() => {
    formik.resetForm();
  }, [forceResetForm]);

  const toBottom = () => {
    if (
      guestChat?.messages?.length > 0 &&
      chatContainerRef.current &&
      hasSidebar
    ) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
    if (guestChat?.messages?.length > 0 && responseRef.current && !hasSidebar) {
      responseRef.current.scrollTo({
        top: responseRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };
  useEffect(() => {
    toBottom();
    setTimeout(() => {
      toBottom();
    }, 100);
  }, [guestChat]);

  useEffect(() => {
    const updateHeight = () => {
      let height = 0;

      if (suggestedQuestionsRef.current && suggestedQuestions.length > 0) {
        height = suggestedQuestionsRef.current.offsetHeight;
      }

      document.documentElement.style.setProperty(
        '--suggested-questions-height',
        `${height}px`
      );
    };

    updateHeight();

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [suggestedQuestions, guestChat]);
  const resetIdleTimer = useCallback(() => {
    if (!isOpen || !idleTime || hasActiveForm || isIdleCalledLast) return;

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    setTimer(0);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev >= idleTime - 1) {
          clearInterval(intervalRef.current);
          return idleTime;
        }
        return prev + 1;
      });
    }, 1000);

    idleTimerRef.current = setTimeout(() => {
      if (guestChat?.chatID) {
        setIsIdleCalledLast(true);
        handleIdleSubmit();
      }
    }, idleTime * 1000);
  }, [isOpen, idleTime, hasActiveForm, guestChat?.chatID, handleIdleSubmit]);

  useEffect(() => {
    resetIdleTimer();
    return () => {
      clearTimeout(idleTimerRef.current);
      clearInterval(intervalRef.current);
    };
  }, [
    resetIdleTimer,
    formik.values.Question,
    formik.values.audio,
    JSON.stringify(formik.values.files),
    JSON.stringify(formik.values.images),
  ]);

  const bodyView = (
    <div
      className={`${styles['chat-body']} ${
        disableConsole ? styles['chat-body-full'] : null
      } ${isChatting ? styles['chat-on'] : ''} ${bodyClassName}`}
    >
      {isFetchingChat && (
        <div className={styles['chat-loader']}>
          <img src={chatLoader} alt='Loading...' />
        </div>
      )}

      <div
        ref={chatContainerRef}
        className={`${styles['start-chat-container']} ${
          isWorkFlow ? styles['tryMe'] : ''
        } ${!isChatting ? styles['opened'] : ''} ${className ?? ''}`}
      >
        <div className={`${styles['chatting-body']} ${chattingClassName}`}>
          <div
            className={`${styles['chat-response']} ${
              isWorkFlow ? styles['workflow-response'] : ''
            }`}
            ref={responseRef}
          >
            {guestChat?.messages.map((val, index) => {
              const textFromArray = Array.isArray(val?.content)
                ? val.content
                    .map((c) => (typeof c?.value === 'string' ? c.value : ''))
                    .join(' ')
                    .trim()
                : null;
              const textFromString =
                typeof val?.content === 'string' ? val.content.trim() : '';

              const normalized = (textFromArray ?? textFromString) || '';

              if (val.role === 'user' && normalized.length === 0) {
                return null;
              }

              return val.role === 'user' ? (
                <UserRequest key={index} chatData={val} userData={userData} />
              ) : (
                <BotResponse
                  key={index}
                  chatData={val}
                  isWorkFlow={isWorkFlow}
                  isRequesting={isRequesting}
                  chatId={chatId}
                  botId={bot}
                  setGuestChat={setGuestChat}
                  setCapture={setCapture}
                  guestChat={guestChat}
                  setSuggestedQuestions={setSuggestedQuestions}
                  settings={botSettingsData}
                  isExpanded={isExpanded}
                  direction={guestChat.direction}
                  restartForm={restartForm}
                  disableConsole={disableConsole}
                  isConversation={isConversation}
                  onHasFormChange={setHasActiveForm}
                  triggerNewConversation={triggerNewConversation}
                  selectedBot={selectedBot}
                />
              );
            })}
          </div>
          {suggestedQuestions?.length > 0 && !isSubmitChatLoading && (
            <div
              ref={suggestedQuestionsRef}
              className={styles['suggested-questions']}
            >
              {suggestedQuestions?.map((val, index) => {
                if (val === '') return;
                return (
                  <div key={index} className={styles['questions-response']}>
                    <div
                      key={`question-${index}`}
                      className={styles['question']}
                      onClick={() => {
                        if (isRequesting) return;
                        formik.setFieldValue('Question', val);
                        formik.handleSubmit();
                      }}
                    >
                      <p>{val}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {!disableConsole ? (
        <div
          className={`${styles['chat-input-container']} ${inputContainerClass}`}
        >
          <ChatInput
            isRequesting={isRequesting}
            capture={capture}
            submitForm={handleSubmit}
            isSubmitChatLoading={isSubmitChatLoading}
            forceResetForm={forceResetForm}
            hasSidebar={hasSidebar}
            settings={botSettingsData?.data}
          />
        </div>
      ) : null}
    </div>
  );
  return (
    <FormikProvider value={formik}>
      {isWorkFlow || disableConsole ? (
        bodyView
      ) : (
        <div className={!isWorkFlow ? styles['body'] : null}>
          {!isWorkFlow && (
            <div className={styles.header}>
              <ChatHeader
                title='Lebanese American University'
                selectedBot={selectedBot}
              />
            </div>
          )}
          {bodyView}
        </div>
      )}

      {/* {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            fontSize: '0.8rem',
            color: 'gray',
          }}
        >
          Idle Timeout: {timer}s / {idleTime}s
        </div>
      )} */}
    </FormikProvider>
  );
};

export default ChatBody;
